import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import api from "../api/axios";

import PatientsToolbar from "../components/PatientsToolbar";
import PatientWizard from "../components/PatientWizard";
import PatientCard from "../components/PatientCard";
import "./PatientsPage.responsive.css";

function getPatientModelPath(patient) {
  const gender = String(patient?.gender || "").toLowerCase();

  if (
    gender.includes("female") ||
    gender.includes("woman") ||
    gender.includes("mahila") ||
    gender.includes("महिला")
  ) {
    return "/models/hra/female-united.glb";
  }

  return "/models/hra/male-united.glb";
}

function PatientHraViewer({ patient }) {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const animationRef = useRef(null);
  const modelRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [modelError, setModelError] = useState("");

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !patient) return;

    setLoading(true);
    setModelError("");

    const width = mount.clientWidth || 360;
    const height = mount.clientHeight || 360;

    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(32, width / height, 0.1, 100);

    /*
      Camera is slightly higher and farther.
      Model itself is also moved up after loading.
    */
    camera.position.set(0, 1.62, 4.45);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });

    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    rendererRef.current = renderer;
    mount.innerHTML = "";
    mount.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1.7);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0x9fdcff, 2.5);
    keyLight.position.set(3, 5, 4);
    scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0x18b7ff, 1.85);
    rimLight.position.set(-3, 2, -3);
    scene.add(rimLight);

    const fillLight = new THREE.PointLight(0x1f7bc1, 2.25, 8);
    fillLight.position.set(0, 1.4, 2.5);
    scene.add(fillLight);

    const loader = new GLTFLoader();
    const modelPath = getPatientModelPath(patient);

    loader.load(
      modelPath,
      (gltf) => {
        const model = gltf.scene;
        modelRef.current = model;

        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();

        box.getSize(size);
        box.getCenter(center);

        model.position.sub(center);

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = maxDim > 0 ? 2.75 / maxDim : 1;

        model.scale.setScalar(scale);

        /*
          Key visual fix:
          earlier model was sitting too low.
          This moves it upward in the card.
        */
        model.position.y = 0.52;

        model.traverse((child) => {
          if (!child.isMesh) return;

          child.castShadow = true;
          child.receiveShadow = true;

          if (child.material) {
            child.material.side = THREE.DoubleSide;

            if (child.material.color) {
              child.material.color.lerp(new THREE.Color("#9fdcff"), 0.08);
            }

            child.material.needsUpdate = true;
          }
        });

        scene.add(model);
        setLoading(false);
      },
      undefined,
      (error) => {
        console.error("HRA model load failed:", error);
        setModelError(`Could not load ${modelPath}`);
        setLoading(false);
      }
    );

    const clock = new THREE.Clock();

    const animate = () => {
      const elapsed = clock.getElapsedTime();

      if (modelRef.current) {
        modelRef.current.rotation.y = Math.sin(elapsed * 0.42) * 0.2;
        modelRef.current.position.y = 0.52 + Math.sin(elapsed * 1.25) * 0.035;
      }

      renderer.render(scene, camera);
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      if (!mount || !rendererRef.current) return;

      const nextWidth = mount.clientWidth || 360;
      const nextHeight = mount.clientHeight || 360;

      camera.aspect = nextWidth / nextHeight;
      camera.updateProjectionMatrix();
      rendererRef.current.setSize(nextWidth, nextHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();

        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => material.dispose?.());
          } else {
            object.material.dispose?.();
          }
        }
      });

      if (rendererRef.current) {
        rendererRef.current.dispose();
      }

      mount.innerHTML = "";
      modelRef.current = null;
      rendererRef.current = null;
    };
  }, [patient?.id, patient?.gender]);

  if (!patient) {
    return (
      <div className="mgpatients-graph-idle">
        <svg className="mgpatients-graph-lines" viewBox="0 0 420 300">
          <path d="M210 150 C120 80, 85 110, 70 70" />
          <path d="M210 150 C310 65, 350 95, 355 70" />
          <path d="M210 150 C120 235, 85 210, 72 238" />
          <path d="M210 150 C320 230, 350 205, 355 236" />
          <path d="M70 70 C180 20, 270 20, 355 70" />
          <path d="M72 238 C175 285, 275 285, 355 236" />
        </svg>

        <div className="mgpatients-graph-core">
          <i />
          <span />
          <b />
        </div>

        <div className="mgpatients-graph-node n1">Profile</div>
        <div className="mgpatients-graph-node n2">Family</div>
        <div className="mgpatients-graph-node n3">Session</div>
        <div className="mgpatients-graph-node n4">Brain</div>
      </div>
    );
  }

  return (
    <div className="mgpatients-hra-viewer">
      <div ref={mountRef} className="mgpatients-hra-canvas" />

      {loading && (
        <div className="mgpatients-hra-loader">Loading anatomy model...</div>
      )}

      {modelError && <div className="mgpatients-hra-error">{modelError}</div>}
    </div>
  );
}

function PatientStatCard({ label, value, helper, primary = false }) {
  return (
    <div className={primary ? "mgpatients-stat primary" : "mgpatients-stat"}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{helper}</p>
    </div>
  );
}

function PatientBodyVisual({ stats, selectedPatient, onClearSelectedPatient }) {
  return (
    <div className="mgpatients-body-card">
      <div className="mgpatients-body-head">
        <div>
          <span>{selectedPatient ? "Patient Anatomy" : "Patient Graph"}</span>
          <strong>
            {selectedPatient ? selectedPatient.name : "Health profile map"}
          </strong>
        </div>

        <div className="mgpatients-body-head-actions">
          <em>{selectedPatient ? "Selected" : "Live"}</em>

          {selectedPatient && (
            <button
              type="button"
              className="mgpatients-clear-mini"
              onClick={onClearSelectedPatient}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="mgpatients-model-stage">
        <PatientHraViewer patient={selectedPatient} />

        <div className="mgpatients-anatomy-overlay">
          <span>{selectedPatient ? "Selected Patient" : "Profile Focus"}</span>
          <strong>
            {selectedPatient ? selectedPatient.name : "No patient selected"}
          </strong>
          <p>
            {selectedPatient
              ? `${selectedPatient.age || "—"} yrs · ${
                  selectedPatient.gender || "—"
                } · ${selectedPatient.relation || "self"}`
              : "Click a patient card below to focus the anatomy workspace."}
          </p>

          {selectedPatient && (
            <button
              type="button"
              className="mgpatients-clear-overlay"
              onClick={onClearSelectedPatient}
            >
              Clear selected patient
            </button>
          )}
        </div>
      </div>

      <div className="mgpatients-body-metrics">
        <div>
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div>
          <span>Family</span>
          <strong>{stats.relatives}</strong>
        </div>
        <div>
          <span>Senior</span>
          <strong>{stats.seniors}</strong>
        </div>
      </div>
    </div>
  );
}

function PatientDirectoryIntelligence({
  stats,
  selectedPatient,
  onClearSelectedPatient,
}) {
  const maxValue = Math.max(stats.total, stats.relatives, stats.seniors, 1);

  const bars = [
    {
      label: "Profiles",
      value: stats.total,
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "Relatives",
      value: stats.relatives,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Seniors",
      value: stats.seniors,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
  ];

  let nextAction = "Create the first patient profile";

  if (stats.total > 0) {
    nextAction = "Select a patient profile to inspect health context";
  }

  if (selectedPatient) {
    nextAction = `Continue intake or session review for ${selectedPatient.name}`;
  }

  if (!selectedPatient && stats.seniors > 0) {
    nextAction = "Review senior patient profiles first";
  }

  return (
    <div className="mgpatients-intel-card">
      <div className="mgpatients-intel-head">
        <div>
          <span>Directory Intelligence</span>
          <strong>Family health workspace</strong>
        </div>
        <em>{stats.total} profiles</em>
      </div>

      <div className="mgpatients-flow">
        <div className="mgpatients-flow-step active">
          <b>1</b>
          <span>Profile</span>
        </div>

        <div
          className={
            stats.total > 0
              ? "mgpatients-flow-step active"
              : "mgpatients-flow-step"
          }
        >
          <b>2</b>
          <span>Relation</span>
        </div>

        <div
          className={
            selectedPatient
              ? "mgpatients-flow-step active"
              : "mgpatients-flow-step"
          }
        >
          <b>3</b>
          <span>Selected</span>
        </div>

        <div
          className={
            stats.relatives > 0
              ? "mgpatients-flow-step active"
              : "mgpatients-flow-step"
          }
        >
          <b>4</b>
          <span>Family Map</span>
        </div>
      </div>

      <div className="mgpatients-intel-bottom">
        <div className="mgpatients-mini-chart">
          {bars.map((bar) => {
            const height = Math.max(
              20,
              Math.round((bar.value / maxValue) * 100)
            );

            return (
              <div className="mgpatients-mini-item" key={bar.label}>
                <div className="mgpatients-mini-track">
                  <i
                    style={{
                      height: `${height}%`,
                      background: bar.color,
                    }}
                  />
                </div>
                <b>{bar.value}</b>
                <small>{bar.label}</small>
              </div>
            );
          })}
        </div>

        <div className="mgpatients-next-action">
          <span>Next Action</span>
          <strong>{nextAction}</strong>
          <p>
            Patient selection updates anatomy focus, family context, and future
            clinical workflow state.
          </p>

          {selectedPatient && (
            <div className="mgpatients-selected-strip">
              <div>
                <span>Selected Patient</span>
                <strong>{selectedPatient.name}</strong>
                <small>
                  {selectedPatient.age || "—"} yrs ·{" "}
                  {selectedPatient.gender || "—"} ·{" "}
                  {selectedPatient.relation || "self"}
                </small>
              </div>

              <button
                type="button"
                className="mgpatients-clear-strip"
                onClick={onClearSelectedPatient}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [relationFilter, setRelationFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [showAll, setShowAll] = useState(false);

  const [showWizard, setShowWizard] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  useEffect(() => {
    loadPatients();
  }, []);

  const clearSelectedPatient = () => {
    setSelectedPatient(null);
  };

  const loadPatients = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/patient/");
      const loadedPatients = res.data || [];

      setPatients(loadedPatients);

      setSelectedPatient((prev) => {
        if (!prev) return null;
        return loadedPatients.find((p) => p.id === prev.id) || null;
      });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load patients");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePatient = async (patient) => {
    const confirmed = window.confirm(
      `Delete patient "${patient.name}" and all linked sessions?`
    );
    if (!confirmed) return;

    try {
      await api.delete(`/api/patient/${patient.id}`);

      setPatients((prev) => {
        const next = prev.filter((p) => p.id !== patient.id);

        if (selectedPatient?.id === patient.id) {
          setSelectedPatient(null);
        }

        return next;
      });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not delete patient");
    }
  };

  const filteredPatients = useMemo(() => {
    let next = [...patients];

    next = next.filter((patient) => {
      const q = search.trim().toLowerCase();

      const matchesSearch =
        !q ||
        patient.name?.toLowerCase().includes(q) ||
        patient.gender?.toLowerCase().includes(q) ||
        (patient.relation || "self").toLowerCase().includes(q);

      const matchesRelation =
        relationFilter === "all" ||
        (patient.relation || "self").toLowerCase() ===
          relationFilter.toLowerCase();

      const matchesGender =
        genderFilter === "all" ||
        (patient.gender || "").toLowerCase() === genderFilter.toLowerCase();

      return matchesSearch && matchesRelation && matchesGender;
    });

    next.sort((a, b) => {
      if (sortBy === "name_asc") {
        return (a.name || "").localeCompare(b.name || "");
      }

      if (sortBy === "name_desc") {
        return (b.name || "").localeCompare(a.name || "");
      }

      if (sortBy === "age_desc") {
        return Number(b.age || 0) - Number(a.age || 0);
      }

      if (sortBy === "age_asc") {
        return Number(a.age || 0) - Number(b.age || 0);
      }

      return Number(b.id || 0) - Number(a.id || 0);
    });

    return next;
  }, [patients, search, relationFilter, genderFilter, sortBy]);

  const visiblePatients = useMemo(() => {
    return showAll ? filteredPatients : filteredPatients.slice(0, 8);
  }, [filteredPatients, showAll]);

  const stats = useMemo(() => {
    return {
      total: patients.length,
      relatives: patients.filter((p) => (p.relation || "self") !== "self")
        .length,
      seniors: patients.filter((p) => Number(p.age || 0) >= 60).length,
    };
  }, [patients]);

  return (
    <div className="mgpatients-page">
      <style>{patientsCss}</style>

      <section className="mgpatients-hero">
        <div className="mgpatients-hero-copy">
          <div className="mgpatients-kicker">Patients</div>
          <h1>Patient Directory</h1>
          <p>
            Manage patient profiles, family relations, senior records, and linked
            clinical workflows from one clean MedGen workspace.
          </p>

          <div className="mgpatients-actions">
            <button
              className="mgpatients-btn primary"
              type="button"
              onClick={() => {
                setEditingPatient(null);
                setShowWizard(true);
              }}
            >
              + Add Patient
            </button>

            <button
              className="mgpatients-btn soft"
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              disabled={loading}
            >
              {showAll ? "Show less" : "See all profiles"}
            </button>
          </div>

          <PatientDirectoryIntelligence
            stats={stats}
            selectedPatient={selectedPatient}
            onClearSelectedPatient={clearSelectedPatient}
          />
        </div>

        <PatientBodyVisual
          stats={stats}
          selectedPatient={selectedPatient}
          onClearSelectedPatient={clearSelectedPatient}
        />
      </section>

      <section className="mgpatients-stats-grid">
        <PatientStatCard
          primary
          label="Total patients"
          value={stats.total}
          helper="Profiles available in the workspace"
        />

        <PatientStatCard
          label="Linked relatives"
          value={stats.relatives}
          helper="Family members connected to the account"
        />

        <PatientStatCard
          label="Senior profiles"
          value={stats.seniors}
          helper="Patients aged 60 or above"
        />
      </section>

      <div className="mgpatients-toolbar-wrap">
        <PatientsToolbar
          search={search}
          onSearchChange={setSearch}
          relationFilter={relationFilter}
          onRelationFilterChange={setRelationFilter}
          genderFilter={genderFilter}
          onGenderFilterChange={setGenderFilter}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          showAll={showAll}
          onToggleShowAll={() => setShowAll((prev) => !prev)}
          onAddPatient={() => {
            setEditingPatient(null);
            setShowWizard(true);
          }}
        />
      </div>

      {loading ? (
        <div className="mgpatients-empty">Loading patients...</div>
      ) : filteredPatients.length === 0 ? (
        <div className="mgpatients-empty">
          <h3>No patients found</h3>
          <p>Try changing filters or create the first patient profile.</p>
          <button
            className="mgpatients-btn primary"
            type="button"
            onClick={() => {
              setEditingPatient(null);
              setShowWizard(true);
            }}
          >
            Create Patient
          </button>
        </div>
      ) : (
        <div className="patients-grid patients-page-grid mgpatients-grid">
          {visiblePatients.map((patient) => (
            <div
              key={patient.id}
              className={
                selectedPatient?.id === patient.id
                  ? "mgpatients-card-wrap selected"
                  : "mgpatients-card-wrap"
              }
              role="button"
              tabIndex={0}
              onClick={() => setSelectedPatient(patient)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSelectedPatient(patient);
              }}
            >
              <PatientCard
                patient={patient}
                selected={selectedPatient?.id === patient.id}
                onSelect={() => setSelectedPatient(patient)}
                onEdit={(p) => {
                  setEditingPatient(p);
                  setShowWizard(true);
                }}
                onDelete={handleDeletePatient}
              />
            </div>
          ))}
        </div>
      )}

      {showWizard && (
        <PatientWizard
          patient={editingPatient}
          onClose={() => {
            setShowWizard(false);
            setEditingPatient(null);
          }}
          onSaved={(savedPatient) => {
            setPatients((prev) => {
              const exists = prev.some((p) => p.id === savedPatient.id);

              if (exists) {
                return prev.map((p) =>
                  p.id === savedPatient.id ? savedPatient : p
                );
              }

              return [savedPatient, ...prev];
            });

            setSelectedPatient(savedPatient);
            setShowWizard(false);
            setEditingPatient(null);
          }}
        />
      )}
    </div>
  );
}

const patientsCss = `
.mgpatients-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;

  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
}

.mgpatients-page * {
  box-sizing: border-box;
}

.mgpatients-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 26px;
  align-items: stretch;
  margin-bottom: 22px;
  padding: 30px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--mg-border);
  box-shadow: 0 20px 55px rgba(7, 17, 31, 0.07);
}

.mgpatients-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgpatients-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(38px, 4vw, 58px);
  line-height: 1.02;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgpatients-hero p {
  max-width: 760px;
  margin: 18px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgpatients-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 22px;
}

.mgpatients-btn {
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgpatients-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgpatients-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgpatients-btn.soft,
.mgpatients-btn.ghost {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

/* Anatomy card */

.mgpatients-body-card {
  position: relative;
  overflow: hidden;
  min-height: 560px;
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 50% 10%, rgba(24, 183, 255, 0.26), transparent 34%),
    radial-gradient(circle at 12% 92%, rgba(31, 123, 193, 0.22), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgpatients-body-card::before {
  content: "";
  position: absolute;
  inset: 20px;
  border-radius: 22px;
  border: 1px solid rgba(24, 183, 255, 0.12);
  pointer-events: none;
  z-index: 1;
}

.mgpatients-body-head {
  position: relative;
  z-index: 5;
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgpatients-body-head span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgpatients-body-head strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgpatients-body-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mgpatients-body-head em {
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgpatients-clear-mini,
.mgpatients-clear-overlay,
.mgpatients-clear-strip {
  border: 1px solid rgba(24,183,255,0.22);
  background: rgba(255,255,255,0.08);
  color: #bfeaff;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 900;
  transition: 0.16s ease;
}

.mgpatients-clear-mini {
  min-height: 29px;
  padding: 5px 10px;
  font-size: 11px;
}

.mgpatients-clear-mini:hover,
.mgpatients-clear-overlay:hover,
.mgpatients-clear-strip:hover {
  background: rgba(24,183,255,0.18);
  color: white;
}

.mgpatients-model-stage {
  position: relative;
  z-index: 2;
  height: 370px;
  margin-top: 12px;
  border-radius: 22px;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 34%, rgba(24,183,255,0.14), transparent 38%),
    rgba(255,255,255,0.03);
  border: 1px solid rgba(24,183,255,0.10);
}

.mgpatients-hra-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 370px;
}

.mgpatients-hra-canvas {
  width: 100%;
  height: 100%;
  min-height: 370px;
  transform: translateY(-54px);
}

.mgpatients-hra-canvas canvas {
  display: block;
  width: 100% !important;
  height: 118% !important;
}

.mgpatients-hra-loader,
.mgpatients-hra-error {
  position: absolute;
  left: 14px;
  right: 14px;
  top: 14px;
  z-index: 8;
  padding: 11px 13px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 850;
  backdrop-filter: blur(12px);
}

.mgpatients-hra-loader {
  color: #bfeaff;
  background: rgba(24, 183, 255, 0.12);
  border: 1px solid rgba(24, 183, 255, 0.18);
}

.mgpatients-hra-error {
  color: #fecaca;
  background: rgba(239, 68, 68, 0.13);
  border: 1px solid rgba(239, 68, 68, 0.22);
}

.mgpatients-anatomy-overlay {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 10px;
  z-index: 6;
  padding: 12px 13px;
  border-radius: 16px;
  background: rgba(3, 6, 12, 0.68);
  border: 1px solid rgba(24,183,255,0.18);
  backdrop-filter: blur(12px);
}

.mgpatients-anatomy-overlay span {
  display: block;
  margin-bottom: 5px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgpatients-anatomy-overlay strong {
  display: block;
  color: white;
  font-size: 15px;
  font-weight: 950;
}

.mgpatients-anatomy-overlay p {
  margin: 5px 0 0;
  color: #a9bdd3;
  font-size: 12px;
  line-height: 1.35;
}

.mgpatients-clear-overlay {
  margin-top: 9px;
  min-height: 30px;
  padding: 6px 11px;
  font-size: 11px;
}

.mgpatients-body-metrics {
  position: relative;
  z-index: 5;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 14px;
}

.mgpatients-body-metrics div {
  padding: 14px;
  border-radius: 16px;
  background: rgba(255,255,255,0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgpatients-body-metrics span {
  display: block;
  margin-bottom: 6px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgpatients-body-metrics strong {
  color: white;
  font-size: 24px;
  font-weight: 950;
}

/* Default graph */

.mgpatients-graph-idle {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 370px;
  overflow: hidden;
}

.mgpatients-graph-lines {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.mgpatients-graph-lines path {
  fill: none;
  stroke: rgba(24, 183, 255, 0.28);
  stroke-width: 2;
  stroke-dasharray: 8 10;
  animation: mgpatientsGraphDash 2.8s linear infinite;
}

.mgpatients-graph-core {
  position: absolute;
  left: 50%;
  top: 48%;
  width: 116px;
  height: 116px;
  transform: translate(-50%, -50%);
  z-index: 3;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background:
    radial-gradient(circle at 32% 24%, rgba(255,255,255,0.32), transparent 24%),
    linear-gradient(145deg, #18b7ff, #1f7bc1);
  box-shadow:
    0 0 0 12px rgba(24,183,255,0.08),
    0 24px 65px rgba(24,183,255,0.22);
  animation: mgpatientsGraphPulse 2.4s ease-in-out infinite;
}

.mgpatients-graph-core i,
.mgpatients-graph-core span,
.mgpatients-graph-core b {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.34);
}

.mgpatients-graph-core i {
  width: 78px;
  height: 78px;
}

.mgpatients-graph-core span {
  width: 48px;
  height: 48px;
}

.mgpatients-graph-core b {
  width: 16px;
  height: 16px;
  background: white;
  border: none;
  box-shadow: 0 0 22px rgba(255,255,255,0.7);
}

.mgpatients-graph-node {
  position: absolute;
  z-index: 4;
  min-width: 82px;
  padding: 9px 11px;
  border-radius: 999px;
  color: white;
  text-align: center;
  font-size: 11px;
  font-weight: 950;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(24,183,255,0.18);
  backdrop-filter: blur(12px);
  box-shadow: 0 14px 34px rgba(3,6,12,0.18);
  animation: mgpatientsGraphFloat 3s ease-in-out infinite;
}

.mgpatients-graph-node.n1 {
  left: 24px;
  top: 48px;
}

.mgpatients-graph-node.n2 {
  right: 24px;
  top: 48px;
  animation-delay: 0.2s;
}

.mgpatients-graph-node.n3 {
  left: 24px;
  bottom: 72px;
  animation-delay: 0.4s;
}

.mgpatients-graph-node.n4 {
  right: 24px;
  bottom: 72px;
  animation-delay: 0.6s;
}

/* Intelligence panel */

.mgpatients-intel-card {
  margin-top: 10px;
  padding: 20px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 96% 0%, rgba(24, 183, 255, 0.13), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.055);
}

.mgpatients-intel-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.mgpatients-intel-head span {
  display: block;
  margin-bottom: 7px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.mgpatients-intel-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 22px;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.mgpatients-intel-head em {
  padding: 6px 11px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-weight: 900;
  font-style: normal;
  white-space: nowrap;
}

.mgpatients-flow {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.mgpatients-flow::before {
  content: "";
  position: absolute;
  left: 10%;
  right: 10%;
  top: 20px;
  height: 3px;
  border-radius: 999px;
  background:
    linear-gradient(
      90deg,
      rgba(31,123,193,0.12),
      rgba(24,183,255,0.42),
      rgba(31,123,193,0.12)
    );
  background-size: 220% 100%;
  animation: mgpatientsFlow 2.8s linear infinite;
}

.mgpatients-flow::after {
  content: "";
  position: absolute;
  top: 16px;
  left: 10%;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #18b7ff;
  box-shadow:
    0 0 0 6px rgba(24,183,255,0.12),
    0 0 22px rgba(24,183,255,0.55);
  animation: mgpatientsFlowDot 4s ease-in-out infinite;
}

.mgpatients-flow-step {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 7px;
  animation: mgpatientsStepFloat 0.7s ease both;
}

.mgpatients-flow-step b {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 15px;
  color: #0b4e84;
  background: #eef6ff;
  border: 1px solid var(--mg-border);
  font-size: 13px;
  font-weight: 950;
}

.mgpatients-flow-step.active b {
  color: white;
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.28), transparent 28%),
    linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  animation: mgpatientsNodeBreath 2.2s ease-in-out infinite;
}

.mgpatients-flow-step span {
  color: var(--mg-text);
  font-size: 12px;
  font-weight: 950;
}

.mgpatients-intel-bottom {
  display: grid;
  grid-template-columns: 310px minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
}

.mgpatients-mini-chart {
  min-height: 150px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: end;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
}

.mgpatients-mini-item {
  display: grid;
  justify-items: center;
  gap: 6px;
}

.mgpatients-mini-track {
  width: 52px;
  height: 82px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 15px;
  background: #dfeaf6;
  border: 1px solid #d1e2f3;
}

.mgpatients-mini-track i {
  display: block;
  width: 100%;
  min-height: 18px;
  border-radius: 11px;
  animation: mgpatientsBarRise 0.7s ease both;
}

.mgpatients-mini-item b {
  color: var(--mg-text);
  font-size: 17px;
  font-weight: 950;
}

.mgpatients-mini-item small {
  color: var(--mg-muted);
  font-size: 10px;
  font-weight: 850;
}

.mgpatients-next-action {
  padding: 18px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 34%),
    #ffffff;
  border: 1px solid #d9e8f6;
}

.mgpatients-next-action span,
.mgpatients-selected-strip span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgpatients-next-action strong {
  display: block;
  color: var(--mg-text);
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.035em;
}

.mgpatients-next-action p {
  margin: 10px 0 0;
  color: var(--mg-muted);
  font-size: 13px;
  line-height: 1.55;
}

.mgpatients-selected-strip {
  margin-top: 14px;
  padding: 13px;
  border-radius: 14px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.mgpatients-selected-strip strong {
  font-size: 16px;
}

.mgpatients-selected-strip small {
  display: block;
  margin-top: 4px;
  color: var(--mg-muted);
  font-weight: 800;
}

.mgpatients-clear-strip {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border-color: rgba(31, 123, 193, 0.18);
  min-height: 32px;
  padding: 7px 12px;
  font-size: 11px;
  flex: 0 0 auto;
}

.mgpatients-clear-strip:hover {
  background: #1f7bc1;
  color: white;
}

/* Stats and cards */

.mgpatients-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.mgpatients-stat {
  min-height: 128px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.94);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgpatients-stat.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #125a94);
  border-color: transparent;
}

.mgpatients-stat span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgpatients-stat.primary span {
  color: rgba(255,255,255,0.75);
}

.mgpatients-stat strong {
  display: block;
  color: var(--mg-text);
  font-size: 28px;
  font-weight: 950;
}

.mgpatients-stat.primary strong {
  color: white;
}

.mgpatients-stat p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgpatients-stat.primary p {
  color: rgba(255,255,255,0.86);
}

.mgpatients-toolbar-wrap {
  margin-bottom: 18px;
}

.mgpatients-page input,
.mgpatients-page select {
  color: var(--mg-text);
}

.mgpatients-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 18px;
}

.mgpatients-card-wrap {
  border-radius: 22px;
  cursor: pointer;
  transition: 0.16s ease;
}

.mgpatients-card-wrap:hover {
  transform: translateY(-2px);
}

.mgpatients-card-wrap.selected {
  box-shadow:
    0 0 0 2px rgba(24,183,255,0.65),
    0 18px 44px rgba(31, 123, 193, 0.16);
}

.mgpatients-page .patient-card,
.mgpatients-page .card {
  border-radius: 20px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  box-shadow: 0 14px 38px rgba(7,17,31,0.06);
  color: var(--mg-text);
}

.mgpatients-empty {
  padding: 24px;
  border-radius: 18px;
  background: rgba(255,255,255,0.95);
  border: 1px solid var(--mg-border);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgpatients-empty h3 {
  margin: 0 0 8px;
  color: var(--mg-text);
}

.mgpatients-empty p {
  color: var(--mg-muted);
}

@keyframes mgpatientsFlow {
  0% {
    background-position: 220% 0;
  }

  100% {
    background-position: -220% 0;
  }
}

@keyframes mgpatientsFlowDot {
  0% {
    left: 10%;
    opacity: 0;
    transform: scale(0.82);
  }

  8% {
    opacity: 1;
    transform: scale(1);
  }

  33% {
    left: 36%;
  }

  66% {
    left: 63%;
  }

  92% {
    opacity: 1;
  }

  100% {
    left: 88%;
    opacity: 0;
    transform: scale(0.82);
  }
}

@keyframes mgpatientsStepFloat {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes mgpatientsNodeBreath {
  0% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }

  50% {
    box-shadow:
      0 18px 36px rgba(31, 123, 193, 0.28),
      0 0 0 7px rgba(24, 183, 255, 0.06);
  }

  100% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }
}

@keyframes mgpatientsBarRise {
  from {
    transform: scaleY(0.4);
    opacity: 0.45;
    transform-origin: bottom;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
    transform-origin: bottom;
  }
}

@keyframes mgpatientsGraphDash {
  from {
    stroke-dashoffset: 0;
  }

  to {
    stroke-dashoffset: -36;
  }
}

@keyframes mgpatientsGraphPulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }

  50% {
    transform: translate(-50%, -50%) scale(1.05);
  }
}

@keyframes mgpatientsGraphFloat {
  0%, 100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-7px);
  }
}

@media (max-width: 1280px) {
  .mgpatients-hero {
    grid-template-columns: 1fr;
  }

  .mgpatients-body-card {
    max-width: 680px;
  }
}

@media (max-width: 1100px) {
  .mgpatients-page {
    padding: 20px;
  }

  .mgpatients-intel-bottom {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .mgpatients-flow {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mgpatients-flow::before,
  .mgpatients-flow::after {
    display: none;
  }

  .mgpatients-flow-step {
    grid-template-columns: 42px 1fr;
    justify-items: start;
    text-align: left;
    padding: 12px;
    border-radius: 16px;
    background: rgba(255,255,255,0.75);
    border: 1px solid #d9e8f6;
  }

  .mgpatients-selected-strip {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 760px) {
  .mgpatients-page {
    padding: 14px;
  }

  .mgpatients-hero {
    padding: 20px;
  }

  .mgpatients-hero h1 {
    font-size: 36px;
  }

  .mgpatients-actions {
    flex-direction: column;
  }

  .mgpatients-btn {
    width: 100%;
  }

  .mgpatients-stats-grid {
    grid-template-columns: 1fr;
  }

  .mgpatients-grid {
    grid-template-columns: 1fr;
  }

  .mgpatients-body-metrics {
    grid-template-columns: 1fr;
  }

  .mgpatients-mini-chart {
    grid-template-columns: 1fr;
  }

  .mgpatients-model-stage,
  .mgpatients-hra-viewer,
  .mgpatients-hra-canvas,
  .mgpatients-graph-idle {
    min-height: 330px;
    height: 330px;
  }

  .mgpatients-hra-canvas {
    transform: translateY(-42px);
  }

  .mgpatients-hra-canvas canvas {
    height: 114% !important;
  }
}
`;